import { UpdateResolver, UpdatesConfig } from "@urql/exchange-graphcache";

import {
  BlueprintExecutionEvent,
  BlueprintValueConsumerReferenceInput,
  ExecuteBlueprintDraftInput,
} from "../schema.graphql";

const onNodeChangeClearNodesAndEdges: UpdateResolver = (
  _,
  { input }: { input: { blueprintVersionId: string } },
  cache,
) => {
  if (!input.blueprintVersionId) return;
  cache.invalidate(
    {
      __typename: "BlueprintVersion",
      id: input.blueprintVersionId,
    },
    "edges",
  );
  cache.invalidate(
    {
      __typename: "BlueprintVersion",
      id: input.blueprintVersionId,
    },
    "nodes",
  );
};

const onEdgeChangeUpdateTarget: UpdateResolver = (
  _,
  {
    input,
  }: {
    input: {
      blueprintVersionId: string;
      target: BlueprintValueConsumerReferenceInput;
    };
  },
  cache,
) => {
  if (!input.blueprintVersionId) return;
  // First invalidate the edges collection
  cache.invalidate(
    {
      __typename: "BlueprintVersion",
      id: input.blueprintVersionId,
    },
    "edges",
  );
  // Then update the target object, as it is affected by the edge change
  if (
    input.target?.type === "NODE_PORT" &&
    !!input.target.nodeInputPort?.nodeId
  ) {
    cache.invalidate(
      {
        __typename: "BlueprintNode",
        id: input.target.nodeInputPort.nodeId,
      },
      "inputs",
    );
  } else if (
    input.target?.type === "BLUEPRINT_PARAMETER" &&
    !!input.blueprintVersionId
  ) {
    cache.invalidate(
      {
        __typename: "BlueprintVersion",
        id: input.blueprintVersionId,
      },
      "outputs",
    );
  }
};

const onExecuteDraftClearLatestRun: UpdateResolver = (
  _,
  { input }: { input: ExecuteBlueprintDraftInput },
  cache,
) => {
  if (!input.blueprintVersionId) return;
  cache.invalidate(
    {
      __typename: "BlueprintVersion",
      id: input.blueprintVersionId,
    },
    "latestRun",
  );
};

const onExecuteStepEventClearRunSteps: UpdateResolver = (
  {
    onBlueprintExecutionEvent,
  }: {
    onBlueprintExecutionEvent: BlueprintExecutionEvent;
  },
  args,
  cache,
) => {
  if (
    onBlueprintExecutionEvent.__typename !== "BlueprintRunStep" ||
    onBlueprintExecutionEvent.status !== "PENDING"
  )
    return;
  cache.invalidate(
    {
      __typename: "BlueprintRun",
      id: onBlueprintExecutionEvent.run.id,
    },
    "steps",
  );
};

const onDeleteBlueprint: UpdateResolver = (
  _,
  { input }: { input: { blueprintId: string } },
  cache,
) => {
  if (!input.blueprintId) return;
  cache.invalidate(
    {
      __typename: "Blueprint",
      id: input.blueprintId,
    },
    "deletedAt",
  );
};

const onDeleteBlueprintDraftClearBlueprintMyDraftVersion: UpdateResolver = (
  _,
  { input }: { input: { blueprintVersionId: string } },
  cache,
) => {
  if (!input.blueprintVersionId) return;

  // Get the parent blueprint cache key from the blueprint version in the cache
  const blueprintCacheKey = cache.resolve(
    { __typename: "BlueprintVersion", id: input.blueprintVersionId },
    "blueprint",
  );

  if (!blueprintCacheKey || typeof blueprintCacheKey !== "string") return;

  // Extract the blueprint ID from the cache key (format: "Blueprint:id")
  const blueprintId = blueprintCacheKey.split(":")[1];

  if (!blueprintId) return;

  cache.invalidate(
    {
      __typename: "Blueprint",
      id: blueprintId,
    },
    "myDraftVersion",
  );
};

export const updatesConfig: Partial<UpdatesConfig> = {
  Mutation: {
    deleteBlueprint: onDeleteBlueprint,
    deleteBlueprintDraft: onDeleteBlueprintDraftClearBlueprintMyDraftVersion,
    // Update blueprint draft node list on node creation/deletion
    addNodeToBlueprint: onNodeChangeClearNodesAndEdges,
    removeNodeFromBlueprint: onNodeChangeClearNodesAndEdges,
    convertBlueprintInputParameterToNode: onNodeChangeClearNodesAndEdges,

    // Update blueprint edges and target node inputs on edge creation/deletion/update
    createBlueprintEdge: onEdgeChangeUpdateTarget,
    updateBlueprintEdge: onEdgeChangeUpdateTarget,
    removeBlueprintEdge: onEdgeChangeUpdateTarget,

    // Clear latest run steps on execute draft mutation
    executeBlueprintDraft: onExecuteDraftClearLatestRun,
  },
  Subscription: {
    onBlueprintExecutionEvent: onExecuteStepEventClearRunSteps,
  },
};
